import React from 'react';

import styled from 'styled-components';

import { useArticleListingUrl } from 'context/articleListingUrl.context';

import { ArticleDate } from 'components/ArticleDate';
import CarouselCard from 'components/Carousel/CarouselCard';
import CarouselSlider from 'components/Carousel/CarouselSlider';
import ContentContainer from 'components/ContentContainer/ContentContainer';
import CtaButton from 'components/CtaButton/CtaButton';
import CtaCard from 'components/CtaCard/CtaCard';
import CtaCardImage from 'components/CtaCard/CtaCardImage';
import InnerHtml from 'components/InnerHtml/InnerHtml';
import FinalCard from 'components/ModularBlocks/ArticleFeedBlock/components/FinalCard';

import { BlogPostI } from 'interfaces/content-stack/blogPost';
import { ArticleFeedBlockI } from 'interfaces/content-stack/modular-blocks/articleFeedBlock';

import { buildBreadcrumbUrls } from 'utils/functions';

const Header = styled.div`
  font-size: 1.5rem;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.fonts.fontFamilySecondary};

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    font-size: 1.25rem;
  }
`;

const StyledContentContainer = styled.div`
  overflow: hidden;
  padding: 4rem 0;

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    padding: 3rem 0;
  }
`;

const Divider = styled.div`
  font-weight: 500;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    display: none;
  }
`;

const HeaderContainer = styled.div`
  gap: 1rem;
  align-items: center;
  display: flex;
  margin-bottom: 2rem;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    gap: 0.5rem;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CtaButtonContainer = styled.div`
  * > {
    @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
      align-self: start;
    }
  }
`;

const ArticleTitle = styled.div`
  font-family: ${(props) => props.theme.fonts.default};
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const ArticlePreview = styled.div<{ $noMargin?: boolean }>`
  font-weight: 500;
  font-family: ${(props) => props.theme.fonts.default};

  p {
    margin-bottom: ${(props) => (props.$noMargin ? '0' : '1rem')};
  }
`;

export interface ArticleFeedBlockProps {
  data: ArticleFeedBlockI;
  blogPost: {
    edges: {
      node: BlogPostI;
    }[];
  };
}

const ArticleFeedBlock: React.FC<ArticleFeedBlockProps> = ({ blogPost, data }) => {
  const { header, cta, carouselCategory, finalCard } = data;
  const { uid, title } = carouselCategory[0];
  const { allArticles } = useArticleListingUrl();
  const allArticlesLink = title
    ? `${allArticles.replace(/\/*$/, '')}/?${title.replaceAll(' ', '+')}=true`
    : allArticles;

  const filteredArticles =
    blogPost?.edges?.filter(({ node: article }) =>
      article.metadata.categories?.some((category) => category.uid === uid)
    ) ?? [];

  return (
    <StyledContentContainer data-elastic-exclude>
      <ContentContainer innerPaddingOverride={'0'}>
        <HeaderContainer>
          {header && <Header>{header}</Header>}
          {header && cta.buttonText && <Divider>|</Divider>}
          {cta.buttonText && (
            <CtaButtonContainer>
              <CtaButton
                bold
                to={allArticlesLink}
                buttonType={cta?.buttonType}
                text={cta?.buttonText}
                dataLayerInfo={header}
              />
            </CtaButtonContainer>
          )}
        </HeaderContainer>
        <CarouselSlider pagination>
          {filteredArticles.slice(0, 9).map(({ node: article }: { node: BlogPostI }, index) => {
            const { breadcrumb, metadata } = article;

            return (
              <CarouselCard key={`${index}-${article.url}`}>
                <CtaCard
                  link={buildBreadcrumbUrls(breadcrumb, article.url)}
                  mobilePadding={'1.5rem 1rem'}
                  fullHeight
                  imgComponent={
                    <CtaCardImage
                      imageUrl={metadata.thumbnailImage?.url}
                      imageAltText={metadata.thumbnailAltText ?? 'Article Image'}
                    />
                  }
                >
                  {article.postDate && <ArticleDate date={article.postDate} />}
                  {article.title && <ArticleTitle>{article.title}</ArticleTitle>}
                  {metadata.excerpt && (
                    <ArticlePreview $noMargin={true}>
                      <InnerHtml richText={metadata.excerpt} />
                    </ArticlePreview>
                  )}
                </CtaCard>
              </CarouselCard>
            );
          })}
          {finalCard?.enabled && <FinalCard finalCard={finalCard} urlHref={allArticlesLink} />}
        </CarouselSlider>
      </ContentContainer>
    </StyledContentContainer>
  );
};

export default ArticleFeedBlock;
